import React, { useEffect, useState } from 'react';
import { healthsherpa } from './api';
import PersonalInfo from './HsPersonalInfo';
import { transfer } from './hsLock';           // 5.44.0: every way into HealthSherpa takes the "in use" lock
import { rowTone } from './hsColors';
import { fs } from './hsScale';

// =============================================================================
// 5.46.0 the client view's coverage (Henry 2026-11-02): YEAR TABS (the 5 most recent plan years + Archive; they also
// drive the notes / to-dos / alerts season), the TOP SUMMARY for the selected year (plan, cost-sharing variant with the
// Verify picker, members covered with masked personal info, dates, status, PAID-THROUGH, premiums, deductible / OOP,
// documents — our stored SBC copy opens in a new tab), "More details", missing / stale flags and COMPARE (two years,
// current vs a quoted plan, a plan vs its next-year crosswalk successor). Our own database only — nothing here calls
// HealthSherpa; "Open in HealthSherpa" goes through the 5.44.0 lock.
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
export const colors = (darkMode) => ({
  panel: rowTone('panel', darkMode), border: darkMode ? '#3a3f46' : '#d5d9df', text: rowTone('text', darkMode),
  sub: rowTone('subtext', darkMode), link: darkMode ? '#7fb2e5' : '#1f5d99', darkMode,
});
const box = (c) => ({ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 8, padding: 14 });
const btn = (c, primary, on) => ({
  padding: '5px 12px', fontSize: fs(13), borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: primary || on ? 600 : 400,
  border: primary || on ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary || on ? '#185FA5' : c.panel, color: primary || on ? '#fff' : c.text,
});
export const usd = (cents) => (cents === null || cents === undefined ? '—'
  : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

// our stored SBC: fetched with the login token, opened as a blob in a new tab (the tab is opened IN the click)
export async function openStoredPdf(path) {
  const w = window.open('', '_blank');
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(res.status === 404 ? 'No stored copy of this SBC yet' : `HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    if (w) w.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } catch (e) {
    if (w) w.close();
    window.alert(e.message);
  }
}

export function DocLinks({ c, links, testid = 'cov-links' }) {
  if (!links) return <span style={{ color: c.sub }}>—</span>;
  const a = (href, label, key) => (href ? <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={{ color: c.link }} data-testid={`${testid}-${key}`}>{label}</a> : null);
  const items = [
    links.sbc_stored && links.plan_id
      ? <button key="sbc" type="button" onClick={() => openStoredPdf(healthsherpa.coverage.sbcPath(links.plan_year, links.plan_id))} data-testid={`${testid}-sbc`}
        style={{ background: 'none', border: 'none', padding: 0, color: c.link, cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}
        title="Our saved copy (kept forever)">{links.sbc_language === 'es' ? 'SBC (Español)' : 'SBC (saved copy)'}</button>
      : a(links.sbc_url, 'SBC (carrier site)', 'sbc-url'),
    a(links.brochure_url, 'Brochure', 'brochure'), a(links.formulary_url, 'Formulary', 'formulary'), a(links.network_url, 'Network', 'network'),
  ].filter(Boolean);
  if (!items.length) return <span style={{ color: c.sub }}>—</span>;
  return <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap' }} data-testid={testid}>{items}</span>;
}

// --- year tabs -------------------------------------------------------------------------------------------------------
export function YearTabs({ c, years, value, onChange }) {
  if (!years) return null;
  const inArchive = !years.tabs.includes(value);
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }} role="tablist" aria-label="Plan year" data-testid="cov-year-tabs">
      {years.tabs.map((y) => (
        <button key={y} role="tab" aria-selected={y === value} style={{ ...btn(c, false, y === value), padding: '4px 12px' }}
          onClick={() => onChange(y)} data-testid={`cov-tab-${y}`}>{y}{y === years.default ? ' •' : ''}</button>
      ))}
      <select aria-label="Archive (older years)" value={inArchive ? String(value) : ''} data-testid="cov-tab-archive"
        onChange={(e) => { if (e.target.value) onChange(Number(e.target.value)); }}
        style={{ ...btn(c, false, inArchive), padding: '4px 8px' }}>
        <option value="">Archive{years.archive.length ? ` (${years.archive.length})` : ''}</option>
        {years.archive.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// --- summary + more details ----------------------------------------------------------------------------------------
function Field({ c, k, children, big, testid }) {
  return (
    <div data-testid={testid}>
      <div style={{ fontSize: fs(11), color: c.sub, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{k}</div>
      <div style={{ fontSize: fs(big ? 20 : 14), fontWeight: big ? 700 : 500, overflowWrap: 'anywhere' }}>{children || <span style={{ color: c.sub }}>—</span>}</div>
    </div>
  );
}

// 5.47.0 admin: no saved SBC copy yet -> upload the PDF by hand (validated on the server; kept forever; never overwrites).
export function SbcUpload({ c, year, planId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const ref = React.useRef(null);
  const go = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true); setMsg(null);
    try {
      const r = await healthsherpa.coverage.uploadSbc(year, planId, f);
      setMsg({ ok: true, text: `Saved — attached to ${r.data.variants.length} variant(s) (${r.data.check})` });
      if (onDone) onDone();
    } catch (err) { setMsg({ ok: false, text: detailOf(err) }); }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} data-testid="cov-sbc-upload">
      <input ref={ref} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={go} data-testid="cov-sbc-upload-input" />
      <button type="button" style={{ ...btn(c), padding: '2px 8px' }} disabled={busy} onClick={() => ref.current && ref.current.click()}
        title="Admins: no saved copy yet — upload the SBC PDF (checked: a PDF that says Summary of Benefits and Coverage + the plan year)">
        {busy ? 'Uploading…' : 'Upload SBC'}</button>
      {msg && <span role={msg.ok ? 'status' : 'alert'} style={{ fontSize: fs(12), color: msg.ok ? '#1e7e34' : '#c0392b' }} data-testid="cov-sbc-upload-msg">{msg.text}</span>}
    </span>
  );
}

function VerifyPicker({ c, personId, year, match, onPicked, label = 'Verify', tone = '#b9770e' }) {
  const [pick, setPick] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setError(null);
    try { await healthsherpa.coverage.pick(personId, year, pick); onPicked(); } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="cov-verify" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ background: tone, color: '#fff', borderRadius: 10, padding: '1px 9px', fontSize: fs(12), fontWeight: 700 }}>{label}</span>
      <select aria-label="Which plan variant" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="cov-verify-pick"
        style={{ padding: '4px 6px', fontSize: fs(12), borderRadius: 4, border: `1px solid ${c.border}`, background: c.panel, color: c.text, maxWidth: 420 }}>
        <option value="">Pick the variant ({match.candidates.length} candidates{match.via === 'crosswalk' ? ', via the CMS crosswalk' : ''})…</option>
        {match.candidates.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.plan_id} · {p.csr_variation || p.variant_name || p.marketing_name} · ded {p.deductible_individual || '—'}</option>)}
      </select>
      <button style={btn(c, true)} disabled={!pick || busy} onClick={save} data-testid="cov-verify-save">Save</button>
      {match.suggested && match.suggested.plan_id && (
        <span style={{ fontSize: fs(12), color: c.sub }} data-testid="cov-suggested" title={match.suggested.label}>
          suggested: <strong>{match.suggested.plan_id}</strong> ({match.suggested.label}{match.suggested.near_cutoff ? ' — near a cutoff, please check' : ''})</span>
      )}
      {match.suggested && !match.suggested.plan_id && (
        <span style={{ fontSize: fs(12), color: c.sub }} data-testid="cov-suggested-none">{match.suggested.label}</span>
      )}
      {error && <span role="alert" style={{ color: '#c0392b', fontSize: fs(12) }}>{error}</span>}
    </div>
  );
}

// 5.47.0 silver CSR auto-pick from income: shows the variant + "auto (from income)"; staff may pick another one.
function AutoPicked({ c, personId, year, match, onPicked }) {
  const [change, setChange] = useState(false);
  return (
    <span data-testid="cov-auto" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{match.plan.csr_variation || match.plan.plan_id} <span style={{ color: c.sub }}>({match.plan.plan_id})</span></span>
      <span style={{ background: '#2d6a4f', color: '#fff', borderRadius: 10, padding: '1px 9px', fontSize: fs(12), fontWeight: 700 }}
        title={match.auto && match.auto.label} data-testid="cov-auto-badge">auto (from income)</span>
      <span style={{ fontSize: fs(12), color: c.sub }}>{match.auto && match.auto.label}</span>
      {!change && <button type="button" style={{ ...btn(c), padding: '2px 8px' }} onClick={() => setChange(true)} data-testid="cov-auto-change">Change…</button>}
      {change && <VerifyPicker c={c} personId={personId} year={year} match={match} onPicked={onPicked} label="Override" tone="#6b7280" />}
    </span>
  );
}

export function useYearView(personId, year, reloadKey) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!year) return undefined;
    let live = true;
    setData(null); setError(null);
    healthsherpa.coverage.year(personId, year).then((r) => { if (live) setData(r.data); }).catch((e) => { if (live) setError(detailOf(e)); });
    return () => { live = false; };
  }, [personId, year, reloadKey, tick]);
  return [data, error, () => setTick((t) => t + 1)];
}

export function Flags({ flags, testid = 'cov-flags' }) {
  if (!flags || !flags.length) return null;
  return (
    <div data-testid={testid} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {flags.map((f) => (
        <span key={f.key} data-flag={f.key} style={{ background: f.key === 'missing' ? '#A32D2D' : '#b9770e', color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: fs(13), fontWeight: 600 }}>
          {f.label}
        </span>
      ))}
    </div>
  );
}

export function CoverageSummary({ personId, year, darkMode, data, error, reload, isAdmin = false }) {
  const c = colors(darkMode);
  if (error) return <div style={box(c)} role="alert">{error}</div>;
  if (!data) return <div style={{ ...box(c), color: c.sub }}>Loading coverage…</div>;
  const s = data.summary;
  const m = data.match;
  return (
    <div style={box(c)} data-testid="cov-summary" data-year={year}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: fs(12), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, flex: 1 }}>Coverage · {year}</div>
        <Flags flags={data.flags} />
        {s && s.hs_link && <button type="button" onClick={() => transfer(s.hs_link)} data-testid="cov-open-hs"
          style={{ background: 'none', border: 'none', padding: 0, color: c.link, cursor: 'pointer', textDecoration: 'underline', fontSize: fs(13) }}>Open in HealthSherpa ↗</button>}
      </div>
      {!s && <div style={{ fontSize: fs(13), color: c.sub }} data-testid="cov-no-data">No HealthSherpa export data for {year}.</div>}
      {s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <Field c={c} k="Paid through" big testid="cov-paid-through">
              {s.paid_through}{s.paid_through_source && <span style={{ fontSize: fs(11), color: c.sub, fontWeight: 400 }}> ({s.paid_through_source === 'api' ? 'HealthSherpa' : 'export'})</span>}
            </Field>
            <Field c={c} k="Status" testid="cov-status">{s.status}</Field>
            <Field c={c} k="Carrier" testid="cov-carrier">{s.carrier}</Field>
            <Field c={c} k="Plan" testid="cov-plan">{s.plan}</Field>
            <Field c={c} k="Metal · type" testid="cov-metal">{[s.metal, s.plan_type].filter(Boolean).join(' · ')}</Field>
            <Field c={c} k="Effective → end" testid="cov-dates">{s.effective ? `${s.effective} → ${s.end || '—'}` : null}</Field>
            <Field c={c} k="Full premium" testid="cov-premium">{usd(s.premium_cents)}</Field>
            <Field c={c} k="APTC" testid="cov-aptc">{usd(s.aptc_cents)}</Field>
            <Field c={c} k="Net premium" testid="cov-net">{usd(s.net_premium_cents)}</Field>
            <Field c={c} k="Deductible (indiv / family)" testid="cov-deductible">{s.deductible_individual ? `${s.deductible_individual} / ${s.deductible_family || '—'}` : null}</Field>
            <Field c={c} k="Out-of-pocket max (indiv / family)" testid="cov-moop">{s.moop_individual ? `${s.moop_individual} / ${s.moop_family || '—'}` : null}</Field>
          </div>
          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
            <div data-testid="cov-variant" data-match={m.status}>
              <span style={{ fontSize: fs(11), color: c.sub, textTransform: 'uppercase' }}>Cost-sharing variant: </span>
              {m.status === 'matched' && <span>{m.plan.csr_variation || m.plan.plan_id} <span style={{ color: c.sub }}>({m.plan.plan_id})</span></span>}
              {m.status === 'picked' && <span>{m.plan.csr_variation || m.plan.plan_id} <span style={{ color: c.sub }}>({m.plan.plan_id} · picked by {m.picked && m.picked.by} {m.picked && m.picked.at_label})</span></span>}
              {m.status === 'verify' && <VerifyPicker c={c} personId={personId} year={year} match={m} onPicked={reload} />}
              {m.status === 'auto' && <AutoPicked c={c} personId={personId} year={year} match={m} onPicked={reload} />}
              {m.status === 'none' && <span style={{ color: '#b9770e' }} data-testid="cov-not-matched">Not matched to the CMS plan data — the export data is shown.</span>}
              {m.status === 'no_data' && <span style={{ color: c.sub }}>—</span>}
            </div>
            <div><span style={{ fontSize: fs(11), color: c.sub, textTransform: 'uppercase' }}>Documents: </span><DocLinks c={c} links={s.links && m.plan ? { ...s.links, plan_year: year, plan_id: m.plan.plan_id } : null} />
              {isAdmin && m.plan && !m.plan.sbc_stored && <> <SbcUpload c={c} year={year} planId={m.plan.plan_id} onDone={reload} /></>}</div>
            <div>
              <div style={{ fontSize: fs(11), color: c.sub, textTransform: 'uppercase', marginBottom: 2 }}>Members covered</div>
              <div style={{ fontSize: fs(13), marginBottom: 4 }} data-testid="cov-members">{(s.members_covered || []).join(', ') || '—'}</div>
              <PersonalInfo personId={personId} planYear={year} darkMode={darkMode} testid="cov-pi" onlyCovered compact />
            </div>
            {s.export_label && <div style={{ fontSize: fs(11), color: c.sub }}>From the HealthSherpa export of {s.export_label}</div>}
          </div>
        </>
      )}
    </div>
  );
}

export function MoreDetails({ darkMode, data }) {
  const c = colors(darkMode);
  const [open, setOpen] = useState(false);
  if (!data || !data.details) return null;
  const d = data.details;
  const row = (k, v, t) => <tr key={k} data-testid={t}><td style={{ color: c.sub, padding: '3px 12px 3px 0', whiteSpace: 'nowrap' }}>{k}</td><td style={{ padding: '3px 0' }}>{v ?? '—'}</td></tr>;
  return (
    <div style={box(c)} data-testid="cov-more">
      <button style={{ ...btn(c), padding: '3px 10px' }} onClick={() => setOpen(!open)} data-testid="cov-more-toggle">{open ? '▾ Hide more details' : '▸ More details'}</button>
      {open && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, fontSize: fs(13) }}>
          <table><tbody>
            {row('Effectuated', d.date_effectuated || yn(d.effectuated), 'cov-more-effectuated')}
            {row('Cancelled', d.date_cancelled)}
            {row('Premium paid', yn(d.premium_paid))}
            {row('Balance due (current / past due)', `${usd(d.api_balance_current_cents ?? d.balance_current_cents)} / ${usd(d.api_balance_past_due_cents ?? d.balance_past_due_cents)}`)}
            {row('Renewal', yn(d.renewal))}
            {row('Carrier policy number', d.carrier_policy_number, 'cov-more-policy')}
            {row('FFM application ID', d.ffm_app_id, 'cov-more-ffm')}
            {row('Consent status', d.consent_status)}
          </tbody></table>
          <table><tbody>
            {row('Network', d.network_name, 'cov-more-network')}
            {row('Primary care', d.copays && d.copays.copay_primary_care, 'cov-more-pcp')}
            {row('Specialist', d.copays && d.copays.copay_specialist)}
            {row('Urgent care', d.copays && d.copays.copay_urgent_care)}
            {row('Generic drugs', d.copays && d.copays.copay_generic_rx)}
            {row('HSA eligible', yn(d.hsa_eligible))}
            {row('Standardized plan', yn(d.standardized))}
          </tbody></table>
          <div style={{ gridColumn: '1 / -1' }} data-testid="cov-more-timeline">
            <div style={{ color: c.sub, marginBottom: 4 }}>Status checks (HealthSherpa)</div>
            {d.timeline.length === 0 && <div style={{ color: c.sub }}>No checks yet.</div>}
            {d.timeline.slice(0, 30).map((t, i) => (
              <div key={i} style={{ fontSize: fs(12) }}>{t.at_label} · {t.source} · {t.result}{t.changed ? ` · changed: ${t.change_summary || ''}` : ''}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- compare --------------------------------------------------------------------------------------------------------
const CMP_ROWS = [
  ['Carrier', 'carrier'], ['Plan', 'plan'], ['Metal', 'metal'], ['Type', 'plan_type'], ['Cost-sharing variant', 'csr_variation'],
  ['Status', 'status'], ['Paid through', 'paid_through'], ['Full premium', 'premium_cents', usd], ['APTC', 'aptc_cents', usd],
  ['Net premium', 'net_premium_cents', usd], ['Deductible', 'deductible_individual'], ['Out-of-pocket max', 'moop_individual'],
  ['Primary care', 'copay_primary_care'], ['Specialist', 'copay_specialist'], ['Urgent care', 'copay_urgent_care'],
  ['Generic drugs', 'copay_generic_rx'], ['Network', 'network_name'],
];

export function ComparePanel({ personId, darkMode, years, current }) {
  const c = colors(darkMode);
  const all = years ? [...years.tabs, ...years.archive] : [];
  const [mode, setMode] = useState('years');
  const [a, setA] = useState(current);
  const [b, setB] = useState(current ? current - 1 : null);
  const [quotes, setQuotes] = useState(null);
  const [qid, setQid] = useState('');
  const [planId, setPlanId] = useState('');
  const [out, setOut] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setA(current); setB(current ? current - 1 : null); }, [current]);
  useEffect(() => {
    if (mode !== 'quote' || quotes) return;
    healthsherpa.coverage.quotes(personId).then((r) => setQuotes(r.data.quotes)).catch((e) => setError(detailOf(e)));
  }, [mode, personId, quotes]);
  const q = quotes ? quotes.find((x) => String(x.quote_id) === String(qid)) : null;
  const run = async () => {
    setBusy(true); setError(null); setOut(null);
    try {
      const body = mode === 'years' ? { mode, a, b } : mode === 'quote' ? { mode, a, quote_id: Number(qid), plan_id: planId } : { mode, a };
      const r = await healthsherpa.coverage.compare(personId, body);
      setOut(r.data);
    } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const sel = { padding: '4px 6px', fontSize: fs(12), borderRadius: 4, border: `1px solid ${c.border}`, background: c.panel, color: c.text };
  const ready = mode === 'years' ? a && b && a !== b : mode === 'quote' ? qid && planId : !!a;
  const side = (x) => (x ? (
    <td style={{ verticalAlign: 'top', padding: 0 }}>
      <table style={{ width: '100%' }}><tbody>
        {CMP_ROWS.map(([k, f, fmt]) => <tr key={f}><td style={{ padding: '3px 8px', overflowWrap: 'anywhere' }}>{fmt ? fmt(x[f]) : (x[f] ?? '—')}</td></tr>)}
        <tr><td style={{ padding: '3px 8px' }}><DocLinks c={c} links={x.links} testid={`cmp-links-${x.label}`} /></td></tr>
      </tbody></table>
    </td>
  ) : null);
  return (
    <div style={box(c)} data-testid="cov-compare">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontSize: fs(12), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub }}>Compare</div>
        {[['years', 'Two years'], ['quote', 'Current vs a quote'], ['crosswalk', 'Plan vs next year']].map(([k, l]) => (
          <button key={k} style={{ ...btn(c, false, mode === k), padding: '3px 10px' }} onClick={() => { setMode(k); setOut(null); setError(null); }} data-testid={`cmp-mode-${k}`}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: fs(13) }}>
        <select aria-label="Year" value={a || ''} onChange={(e) => setA(Number(e.target.value))} style={sel} data-testid="cmp-a">
          {all.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {mode === 'years' && <>vs <select aria-label="Second year" value={b || ''} onChange={(e) => setB(Number(e.target.value))} style={sel} data-testid="cmp-b">
          {all.map((y) => <option key={y} value={y}>{y}</option>)}</select></>}
        {mode === 'quote' && (
          <>
            vs <select aria-label="Quote" value={qid} onChange={(e) => { setQid(e.target.value); setPlanId(''); }} style={sel} data-testid="cmp-quote">
              <option value="">{quotes ? (quotes.length ? 'Pick a quote…' : 'No quotes for this client') : 'Loading…'}</option>
              {(quotes || []).map((x) => <option key={x.quote_id} value={x.quote_id}>#{x.quote_id} · {x.plan_year} · {x.created_label} ({x.tie === 'person' ? 'started from this client' : 'via the InsureDocs client'})</option>)}
            </select>
            {q && <select aria-label="Quoted plan" value={planId} onChange={(e) => setPlanId(e.target.value)} style={{ ...sel, maxWidth: 360 }} data-testid="cmp-quote-plan">
              <option value="">Pick a plan…</option>
              {q.plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.selected ? '★ ' : ''}{p.issuer_name} · {p.name} · {p.metal_level}</option>)}
            </select>}
          </>
        )}
        {mode === 'crosswalk' && <span style={{ color: c.sub }}>vs its {a ? a + 1 : ''} successor (CMS crosswalk)</span>}
        <button style={btn(c, true)} disabled={!ready || busy} onClick={run} data-testid="cmp-run">{busy ? '…' : 'Compare'}</button>
      </div>
      {error && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13), marginTop: 6 }}>{error}</div>}
      {out && out.message && <div style={{ marginTop: 8, fontSize: fs(13), color: '#b9770e' }} data-testid="cmp-message">{out.message}</div>}
      {out && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table data-testid="cmp-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: fs(13) }}>
            <thead><tr><th style={{ textAlign: 'left', padding: '3px 8px' }} /><th style={{ textAlign: 'left', padding: '3px 8px' }} data-testid="cmp-left-label">{out.left && out.left.label}</th>
              <th style={{ textAlign: 'left', padding: '3px 8px' }} data-testid="cmp-right-label">{out.right ? out.right.label : '—'}</th></tr></thead>
            <tbody><tr>
              <td style={{ verticalAlign: 'top', padding: 0 }}><table><tbody>
                {CMP_ROWS.map(([k]) => <tr key={k}><td style={{ padding: '3px 8px', color: c.sub, whiteSpace: 'nowrap' }}>{k}</td></tr>)}
                <tr><td style={{ padding: '3px 8px', color: c.sub }}>Documents</td></tr>
              </tbody></table></td>
              {side(out.left)}
              {out.right ? side(out.right) : <td style={{ verticalAlign: 'top', padding: '3px 8px', color: c.sub }}>{out.message || '—'}</td>}
            </tr></tbody>
          </table>
        </div>
      )}
    </div>
  );
}
